import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


// Built-in pattern presets
const PATTERN_PRESETS = [
  { value: '', label: 'Custom Pattern' },
  { value: '^[a-zA-Z\\s]+$', label: 'Letters Only' },
  { value: '^[0-9]+$', label: 'Numbers Only' },
  { value: '^[a-zA-Z0-9]+$', label: 'Alphanumeric' },
  { value: '^[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}$', label: 'Email Address' },
  { value: '^\\+?[1-9]\\d{1,14}$', label: 'Phone Number (E.164)' },
  { value: '^(https?:\\/\\/)[^\\s/$.?#].[^\\s]*$', label: 'URL' },
  { value: '^\\d{4}-\\d{2}-\\d{2}$', label: 'Date (YYYY-MM-DD)' },
  { value: '^\\d{5}(-\\d{4})?$', label: 'US ZIP Code' },
  { value: '^[A-Z]{1,2}\\d[A-Z\\d]? ?\\d[A-Z]{2}$', label: 'UK Postcode' },
  { value: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$', label: 'Strong Password' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: 'does not contain' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'greaterThan', label: 'is greater than' },
  { value: 'lessThan', label: 'is less than' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
];

const DEPENDENCY_ACTIONS = [
  { value: 'require', label: 'Make this field required' },
  { value: 'hide', label: 'Hide this field' },
  { value: 'show', label: 'Show this field' },
  { value: 'disable', label: 'Disable this field' },
  { value: 'setError', label: 'Show custom error' },
];

const ValidationRulesPanel = ({ element, allElements, onUpdate }) => {
  const [patternPreset, setPatternPreset] = useState('');

  const otherFields = (allElements || [])?.filter(
    (el) => el?.id !== element?.id && el?.name && ['input', 'select', 'checkbox', 'radio']?.includes(el?.type)
  );

  const fieldOptions = otherFields?.map((f) => ({
    value: f?.name || f?.id,
    label: f?.label || f?.name || f?.id,
  }));

  // ── Helpers ──────────────────────────────────────────────────────────────
  const update = (patch) => onUpdate({ ...element, ...patch });

  // ── Validation Rules (per-rule custom messages) ───────────────────────
  const validationRules = element?.validationRules || [];

  const addRule = () => {
    const newRule = {
      id: `rule_${Date.now()}`,
      type: 'minLength',
      value: '',
      message: '',
    };
    update({ validationRules: [...validationRules, newRule] });
  };

  const updateRule = (ruleId, patch) => {
    update({
      validationRules: validationRules?.map((r) =>
        r?.id === ruleId ? { ...r, ...patch } : r
      ),
    });
  };

  const removeRule = (ruleId) => {
    update({ validationRules: validationRules?.filter((r) => r?.id !== ruleId) });
  };

  const ruleTypeOptions = [
    { value: 'minLength', label: 'Min Length' },
    { value: 'maxLength', label: 'Max Length' },
    { value: 'minValue', label: 'Min Value' },
    { value: 'maxValue', label: 'Max Value' },
    { value: 'pattern', label: 'Pattern (Regex)' },
    { value: 'email', label: 'Valid Email' },
    { value: 'url', label: 'Valid URL' },
    { value: 'numeric', label: 'Numeric Only' },
    { value: 'alpha', label: 'Letters Only' },
    { value: 'alphanumeric', label: 'Alphanumeric' },
  ];

  // ── Cross-field Dependencies ──────────────────────────────────────────
  const dependencies = element?.dependencies || [];

  const addDependency = () => {
    const newDep = {
      id: `dep_${Date.now()}`,
      sourceField: '',
      operator: 'equals',
      value: '',
      action: 'require',
      errorMessage: '',
    };
    update({ dependencies: [...dependencies, newDep] });
  };

  const updateDependency = (depId, patch) => {
    update({
      dependencies: dependencies?.map((d) =>
        d?.id === depId ? { ...d, ...patch } : d
      ),
    });
  };

  const removeDependency = (depId) => {
    update({ dependencies: dependencies?.filter((d) => d?.id !== depId) });
  };

  // ── Pattern Preset handler ────────────────────────────────────────────
  const handlePresetChange = (val) => {
    setPatternPreset(val);
    if (val) {
      update({ pattern: val });
    }
  };

  const needsValue = (op) => !['isEmpty', 'isNotEmpty']?.includes(op);

  return (
    <div className="space-y-6">
      {/* ── Basic Required + Error Message ─────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="ShieldCheck" size={15} color="var(--color-primary)" />
          Basic Validation
        </h4>

        <Checkbox
          label="Required Field"
          checked={element?.required || false}
          onChange={(e) => update({ required: e?.target?.checked })}
          description="User must fill this field before submitting"
        />

        <Input
          label="Required Error Message"
          type="text"
          value={element?.requiredMessage || ''}
          onChange={(e) => update({ requiredMessage: e?.target?.value })}
          placeholder="This field is required"
          description="Shown when the field is left empty"
        />
      </div>
      {/* ── Pattern Matching ────────────────────────────────────────── */}
      {element?.type === 'input' && (
        <div className="space-y-3 pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Regex" size={15} color="var(--color-primary)" />
            Pattern Matching
          </h4>

          <Select
            label="Pattern Preset"
            options={PATTERN_PRESETS}
            value={patternPreset}
            onChange={handlePresetChange}
          />

          <Input
            label="Custom Pattern (Regex)"
            type="text"
            value={element?.pattern || ''}
            onChange={(e) => {
              setPatternPreset('');
              update({ pattern: e?.target?.value });
            }}
            placeholder="^[A-Za-z]+$"
            description="Regular expression the value must match"
          />

          {element?.pattern && (
            <div className="p-2 bg-muted/50 rounded text-xs text-muted-foreground font-mono break-all">
              Pattern: {element?.pattern}
            </div>
          )}

          <Input
            label="Pattern Error Message"
            type="text"
            value={element?.patternMessage || ''}
            onChange={(e) => update({ patternMessage: e?.target?.value })}
            placeholder="Please enter a valid value"
          />
        </div>
      )}
      {/* ── Validation Rules (with per-rule messages) ───────────────── */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="ListChecks" size={15} color="var(--color-primary)" />
            Validation Rules
          </h4>
          <button
            onClick={addRule}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <Icon name="Plus" size={13} />
            Add Rule
          </button>
        </div>

        {validationRules?.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No rules yet. Click "Add Rule" to define custom validation.
          </p>
        )}

        {validationRules?.map((rule) => (
          <div
            key={rule?.id}
            className="p-3 bg-muted/30 rounded-md border border-border space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">Rule</span>
              <button
                onClick={() => removeRule(rule?.id)}
                className="p-1 hover:bg-destructive/10 rounded transition-colors"
                aria-label="Remove rule"
              >
                <Icon name="X" size={13} color="var(--color-destructive)" />
              </button>
            </div>

            <Select
              label="Rule Type"
              options={ruleTypeOptions}
              value={rule?.type}
              onChange={(val) => updateRule(rule?.id, { type: val })}
            />

            {['minLength', 'maxLength', 'minValue', 'maxValue', 'pattern']?.includes(rule?.type) && (
              <Input
                label={rule?.type === 'pattern' ? 'Regex Pattern' : 'Value'}
                type={['minLength', 'maxLength', 'minValue', 'maxValue']?.includes(rule?.type) ? 'number' : 'text'}
                value={rule?.value}
                onChange={(e) => updateRule(rule?.id, { value: e?.target?.value })}
                placeholder={rule?.type === 'pattern' ? '^[A-Za-z]+$' : '0'}
              />
            )}

            <Input
              label="Custom Error Message"
              type="text"
              value={rule?.message}
              onChange={(e) => updateRule(rule?.id, { message: e?.target?.value })}
              placeholder={`Validation failed for ${rule?.type}`}
            />
          </div>
        ))}
      </div>
      {/* ── Cross-field Dependencies ─────────────────────────────────── */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="GitBranch" size={15} color="var(--color-primary)" />
            Cross-field Dependencies
          </h4>
          <button
            onClick={addDependency}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <Icon name="Plus" size={13} />
            Add Dependency
          </button>
        </div>

        {otherFields?.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            Add more named fields to the form to create cross-field dependencies.
          </p>
        )}

        {otherFields?.length > 0 && dependencies?.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No dependencies yet. Click "Add Dependency" to link this field to another.
          </p>
        )}

        {dependencies?.map((dep) => (
          <div
            key={dep?.id}
            className="p-3 bg-muted/30 rounded-md border border-border space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">When…</span>
              <button
                onClick={() => removeDependency(dep?.id)}
                className="p-1 hover:bg-destructive/10 rounded transition-colors"
                aria-label="Remove dependency"
              >
                <Icon name="X" size={13} color="var(--color-destructive)" />
              </button>
            </div>

            <Select
              label="Source Field"
              options={[{ value: '', label: 'Select a field…' }, ...fieldOptions]}
              value={dep?.sourceField}
              onChange={(val) => updateDependency(dep?.id, { sourceField: val })}
            />

            <Select
              label="Condition"
              options={CONDITION_OPERATORS}
              value={dep?.operator}
              onChange={(val) => updateDependency(dep?.id, { operator: val })}
            />

            {needsValue(dep?.operator) && (
              <Input
                label="Value"
                type="text"
                value={dep?.value}
                onChange={(e) => updateDependency(dep?.id, { value: e?.target?.value })}
                placeholder="expected value"
              />
            )}

            <Select
              label="Then…"
              options={DEPENDENCY_ACTIONS}
              value={dep?.action}
              onChange={(val) => updateDependency(dep?.id, { action: val })}
            />

            {dep?.action === 'setError' && (
              <Input
                label="Error Message"
                type="text"
                value={dep?.errorMessage}
                onChange={(e) => updateDependency(dep?.id, { errorMessage: e?.target?.value })}
                placeholder="This field has an error based on another field"
              />
            )}
          </div>
        ))}
      </div>
      {/* ── Conditional Visibility ───────────────────────────────────── */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="Eye" size={15} color="var(--color-primary)" />
          Conditional Visibility
        </h4>

        <Checkbox
          label="Enable Conditional Visibility"
          checked={element?.hasVisibilityLogic || false}
          onChange={(e) => update({ hasVisibilityLogic: e?.target?.checked })}
          description="Show or hide this field based on another field's value"
        />

        {element?.hasVisibilityLogic && (
          <div className="space-y-3 p-3 bg-muted/30 rounded-md border border-border">
            <Select
              label="Visibility Action"
              options={[
                { value: 'show', label: 'Show this field when…' },
                { value: 'hide', label: 'Hide this field when…' },
              ]}
              value={element?.visibilityAction || 'show'}
              onChange={(val) => update({ visibilityAction: val })}
            />

            <Select
              label="Source Field"
              options={[{ value: '', label: 'Select a field…' }, ...fieldOptions]}
              value={element?.visibilitySourceField || ''}
              onChange={(val) => update({ visibilitySourceField: val })}
            />

            <Select
              label="Condition"
              options={CONDITION_OPERATORS}
              value={element?.visibilityOperator || 'equals'}
              onChange={(val) => update({ visibilityOperator: val })}
            />

            {needsValue(element?.visibilityOperator || 'equals') && (
              <Input
                label="Value"
                type="text"
                value={element?.visibilityValue || ''}
                onChange={(e) => update({ visibilityValue: e?.target?.value })}
                placeholder="expected value"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationRulesPanel;
